import React, { useEffect, useState } from 'react'
import { Button, Nav, Placeholder, Tab } from 'react-bootstrap'
import { MdOutlineDashboard } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { URL } from '../../config/url'
import { useAuth } from '../../context/auth'
import SessionsServices from '../../services/sessions'
import { buildDashboardQuery, getDashboardTabs, hasTab, readDashboardUrl, withoutTab } from '../../utilities/dashboard'
import TabGeneral from './TabGeneral'
import TabPacientes from './TabPacientes'
import TabProntuarios from './TabProntuarios'
import './style.css'

const EMPTY_PARAMS = {}

const NoAccess = ({ onAgenda }) => (
  <div className='dashboard-card dashboard-no-access'>
    <p>Você não tem acesso ao Dashboard. Peça a liberação no cadastro do seu usuário.</p>
    <Button variant='outline-secondary' onClick={onAgenda}>Ir para a Agenda</Button>
  </div>
)

const DashboardPage = () => {
  const navigate = useNavigate()
  const { user } = useAuth()

  // Abas relidas do /sessions/me ao abrir: a mudança no cadastro vale sem novo login.
  // Estado local, e não setUser: com o RequireAuth dentro do Router, cada setUser remonta a tela
  const [currentUser, setCurrentUser] = useState(user)
  const [loadingUser, setLoadingUser] = useState(true)

  // Aba e filtros na URL (?tab=prontuarios&start_date=...): lidos ao abrir e gravados com replaceState, como na lista de
  // prontuários e no cadastro do paciente. Uma navegação do React Router remontaria a página e faria todas as chamadas de novo.
  // Cada aba guarda os seus filtros; a URL mostra os da aba aberta
  const [initialUrl] = useState(() => readDashboardUrl(window.location.search))
  const [selectedKey, setSelectedKey] = useState(initialUrl.tab)
  const [paramsByTab, setParamsByTab] = useState(() => (initialUrl.tab ? { [initialUrl.tab]: initialUrl.params } : {}))

  useEffect(() => {
    SessionsServices.perfil()
      .then(({ data }) => setCurrentUser(data))
      .catch(() => { })
      .finally(() => setLoadingUser(false))
  }, [])

  const tabs = getDashboardTabs(currentUser)
  const activeKey = hasTab(tabs, selectedKey) ? selectedKey : tabs[0]?.key
  const activeTab = tabs.find((tab) => tab.key === activeKey)

  useEffect(() => {
    if (!activeKey) return
    const query = buildDashboardQuery(activeKey, paramsByTab[activeKey])
    window.history.replaceState(window.history.state, '', `${window.location.pathname}?${query}`)
  }, [activeKey, paramsByTab])

  // 403 numa aba (retirada com a página aberta): esconde a aba; sem nenhuma, mostra a mensagem de sem acesso
  const lostTab = (key) => setCurrentUser((current) => withoutTab(current, key))
  const changeParams = (key) => (params) => setParamsByTab((current) => ({ ...current, [key]: params }))

  return (
    <div className='pb-4 dashboard-page' style={{ '--dashboard-primary': 'var(--app-brand)' }}>
      <div className='d-flex align-items-center gap-3 px-3 pt-3'>
        <div className='dashboard-page-icon'><MdOutlineDashboard /></div>
        <div className='d-flex flex-column textPrimary'>
          <h2 className='m-0'>Dashboard</h2>
          <small className='text-muted'>{activeTab ? `${activeTab.description[0].toUpperCase()}${activeTab.description.slice(1)}` : 'Gestão da clínica'}</small>
        </div>
      </div>

      <div className='px-3 pt-3'>
        {!tabs.length ?
          (loadingUser ?
            <Placeholder as='div' animation='glow' aria-hidden='true'><Placeholder xs={12} className='dashboard-skeleton-chart' style={{ height: 120 }} /></Placeholder>
            :
            <NoAccess onAgenda={() => navigate(URL.calendario)} />)
          :
          // mountOnEnter: cada aba só monta (e busca) quando é aberta, e o ResponsiveContainer não mede largura 0 em aba escondida
          <Tab.Container id='dashboard-tabs' activeKey={activeKey} onSelect={(key) => key && setSelectedKey(key)} mountOnEnter>
            <Nav justify variant='tabs' className='dashboard-tabs'>
              {tabs.map((tab) => (
                <Nav.Item key={tab.key}>
                  <Nav.Link eventKey={tab.key}>{tab.title}</Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
            <Tab.Content className='dashboard-tab-content'>
              {hasTab(tabs, 'GENERAL') ?
                <Tab.Pane eventKey='GENERAL'>
                  <TabGeneral active={activeKey === 'GENERAL'} onForbidden={() => lostTab('GENERAL')} />
                </Tab.Pane>
                : ''}
              {hasTab(tabs, 'PRONTUARIOS') ?
                <Tab.Pane eventKey='PRONTUARIOS'>
                  <TabProntuarios params={paramsByTab.PRONTUARIOS || EMPTY_PARAMS} onParamsChange={changeParams('PRONTUARIOS')} onForbidden={() => lostTab('PRONTUARIOS')} />
                </Tab.Pane>
                : ''}
              {hasTab(tabs, 'PACIENTES') ?
                <Tab.Pane eventKey='PACIENTES'>
                  <TabPacientes params={paramsByTab.PACIENTES || EMPTY_PARAMS} onParamsChange={changeParams('PACIENTES')} onForbidden={() => lostTab('PACIENTES')} />
                </Tab.Pane>
                : ''}
            </Tab.Content>
          </Tab.Container>
        }
      </div>
    </div>
  )
}

export default DashboardPage
